import WarningIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Stack,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  defendantWinChanceMissing,
  enteredChance,
  winChanceEntered,
} from "../../shared/winChance";
import { ClaimTableHead } from "./ClaimTableHead";
import { IssueKindControl } from "./IssueKindControl";
import {
  Claim,
  Defendant,
  RowChances,
  winChanceInputOf,
} from "./IssueModel";
import { IssueWinChanceSummaryCell } from "./IssueRows";
import { JudgedRow } from "./JudgedRow";
import { WinChanceCell } from "./WinChanceCell";
import { WinChanceSlider } from "./WinChanceSlider";

export interface ClaimWinChanceCallbacks {
  onWinChanceUpdated: (defendantId: string, newChance: number) => void;
  onSwitchToGeneric?: () => void;
  onSwitchToSpecific?: () => void;
  onGenericWinChanceUpdated: (newChance: number) => void;
}

export const ClaimWinChanceEditor = ({
  claim,
  defendants,
  callbacks,
  chances,
}: {
  claim: Claim;
  defendants: Defendant[];
  callbacks: ClaimWinChanceCallbacks;
  chances: RowChances;
}) => {
  const { t } = useTranslation();
  // a claim without issues runs against all of its defendants
  const involved = defendants.map(({ id }) => id);
  // the calculation's chances of the claim across its defendants
  const figures = chances.of(claim.id);

  const summaryColumn = defendants.length > 1;

  // a single generic chance only replaces something when several defendants
  // are on the claim, rated or not
  const enableSwitchToGeneric = claim.generic || involved.length > 1;
  const kind = claim.generic ? "generic" : "specific";
  return (
    <>
      <ClaimTableHead
        label={t("case.claims.winChance.label")}
        defendants={defendants}
        markers={{ label: "chances", summary: "summary" }}
        summary={
          summaryColumn
            ? {
                group: t("case.claims.issues.total"),
                first: t("case.claims.issues.againstAll"),
                second: t("case.claims.issues.againstAny"),
              }
            : undefined
        }
      />
      <TableBody>
        <TableRow>
          <TableCell
            sx={{ borderBottom: "none", minWidth: 280, maxWidth: 420 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {claim.title}
            </Typography>
            <JudgedRow issueId={claim.id} />
            {enableSwitchToGeneric && (
              <Box sx={{ mt: 0.75 }}>
                {callbacks.onSwitchToGeneric && callbacks.onSwitchToSpecific ? (
                  <IssueKindControl
                    of="claim"
                    generic={claim.generic === true}
                    onSwitchToGeneric={callbacks.onSwitchToGeneric}
                    onSwitchToSpecific={callbacks.onSwitchToSpecific}
                  />
                ) : (
                  // where the kind cannot be changed it is only stated
                  <Tooltip title={t(`case.claims.kind.hint.claim.${kind}`)}>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {t(`case.claims.kind.${kind}`)}
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            )}
          </TableCell>

          {claim.generic ? (
            <TableCell
              colSpan={defendants.length}
              sx={{
                borderBottom: "none",
              }}
            >
              <Stack
                direction="row"
                spacing={0.75}
                sx={{ alignItems: "center" }}
              >
                {/* the slider reads 0% whether that was chosen or never entered */}
                {!winChanceEntered(winChanceInputOf(claim, involved)) && (
                  <Tooltip
                    title={t("case.prerequisites.winChanceMissing")}
                    arrow
                  >
                    <WarningIcon fontSize="small" color="warning" />
                  </Tooltip>
                )}
                <WinChanceSlider
                  onChanceChange={(newChance) =>
                    callbacks.onGenericWinChanceUpdated(newChance)
                  }
                  chance={claim.genericWinChance ?? 0}
                />
              </Stack>
            </TableCell>
          ) : (
            defendants.map((defendant) => (
              <WinChanceCell
                defendant={defendant}
                winChance={enteredChance(claim.winChanceAgainst, defendant.id)}
                involved={involved.includes(defendant.id)}
                isChanceMissing={defendantWinChanceMissing(
                  winChanceInputOf(claim, involved),
                  defendant.id,
                )}
                onWinChanceUpdated={(defendantId, newChance) =>
                  callbacks.onWinChanceUpdated(defendantId, newChance)
                }
                key={"defendant_winChance_" + defendant.id}
              />
            ))
          )}

          {summaryColumn && (
            <IssueWinChanceSummaryCell
              value={figures?.againstAll}
              calculating={chances.calculating}
            />
          )}
          {summaryColumn && !claim.generic && (
            <IssueWinChanceSummaryCell
              vAny
              value={figures?.againstAny}
              calculating={chances.calculating}
            />
          )}
        </TableRow>
      </TableBody>
    </>
  );
};
